import { Fragment, useContext, useRef, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { EntityContext } from '../../context/EntityContext';
import { UserContext } from '../../context/UserContext';
import { TfiClose } from 'react-icons/tfi';
import { APIUpdateWFSAccess, APICreateWFSAccess, APIGetWFSAccess, APIDeleteWFSAccess } from '../../api/wfs/WFS';
import { MdCheck, MdContentCopy} from "react-icons/md";


export const WfsAccessModal = () => {
    const { user } = useContext(UserContext);
    const cancelButtonRef = useRef(null);
    const { openWFSAccessModal, currentEntity, setOpenWFSAccessModal } = useContext(EntityContext);
    const [copied, setCopied] = useState(null);
    const [isAccessActive, setIsAccessActive] = useState(false);
    const [accessId, setAccessId] = useState(null);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    // Function to generate a random username and password
    const generateCredentials = () => {
        const newUsername = `user_${Math.random().toString(36).substring(2, 14)}`;
        const newPassword = generatePassword();
        setUsername(newUsername);
        setPassword(newPassword);
    };

    // Handle toggling WFS access
    const handleToggleAccess = () => {
        if (!isAccessActive) {
            generateCredentials();
        } else {
            setUsername('');
            setPassword('');
        }
        setIsAccessActive(!isAccessActive);
    };


    function generatePassword(length = 25) {
        const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%-/&*()_+[]{}|,.<>?";
        let password = "";
        for (let i = 0; i < length; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return password;
    }


    // Handle saving WFS access settings
    const handleSaveAccess = async () => {
        const wfsAccessData = {
            type: 'maps',
            entity_id: currentEntity.accessId,
            username: username,
            password: password,
        };

        if (isAccessActive) {
            if (accessId) {
                // Update existing WFS access
                await APIUpdateWFSAccess({ ...wfsAccessData, id: accessId });
            } else {
                // Create new WFS access
                await APICreateWFSAccess(wfsAccessData);
            }
        } else {
            // Delete WFS access
            await APIDeleteWFSAccess(accessId);
        }

        handleCloseModal();
    };

    const handleCloseModal = () => {
        setOpenWFSAccessModal(false);
        setIsAccessActive(false);
        setUsername('');
        setPassword('');
        setAccessId(null);
    };

    useEffect(() => {
        const getAccess = async () => {
            if (currentEntity.id && openWFSAccessModal) {
                const access = await APIGetWFSAccess('maps', currentEntity.accessId);
                if (access) {
                    setIsAccessActive(true);
                    setUsername(access.username);
                    setPassword(access.password);
                    setAccessId(access.id);
                }
            }
        }
        getAccess();
    }, [currentEntity, openWFSAccessModal]);


    return (
        <Transition.Root show={openWFSAccessModal} as={Fragment}>
            <Dialog className="relative z-10" initialFocus={cancelButtonRef} onClose={setOpenWFSAccessModal}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="flex max-w-2xl flex-col max-h-full sm:max-h-max h-full md:h-auto rounded-lg bg-white text-left shadow-xl transition-all w-full relative">
                                <button
                                    type="button"
                                    className="absolute z-40 lg:-top-6 lg:-right-6 top-2 right-2"
                                    onClick={handleCloseModal}
                                    ref={cancelButtonRef}
                                >
                                    <TfiClose className="lg:size-16 border-gray-500/80 border-2 bg-white hover:bg-gray-300 lg:p-4 size-12 p-2 lg:rounded-full rounded-md" />
                                </button>

                                <div className="flex w-full h-full">
                                    <div className="flex flex-col w-full">
                                        <Dialog.Title
                                            as="h3"
                                            className="pt-8 px-8 font-bold text-2xl leading-6 text-gray-900"
                                        >
                                            WFS Zugriff
                                        </Dialog.Title>
                                        <div className="p-8 flex flex-col flex-1 overflow-y-auto">
                                            <p className="text-sm text-gray-600">
                                                XPLANUNG24 ermöglicht es Ihnen, sämtliche Pläne welche der Karte <b>{currentEntity?.name}</b> zugewiesen sind, per WFS abzurufen.
                                            </p>


                                            {/* Show credentials if access is active */}
                                            {isAccessActive && (
                                                <div className="mt-8">
                                                    <div className="mb-4 items-center flex">
                                                        <div className="block text-sm font-medium w-[150px] ">Benutzername:</div>
                                                        <div className="text-gray-900/60  sm:text-sm ">
                                                            {username}
                                                        </div>
                                                    </div>
                                                    <div className="mb-4 items-center flex">
                                                        <div className="block text-sm font-medium w-[150px] ">Passwort:</div>
                                                        <div className="text-gray-900/60  sm:text-sm ">
                                                            {password}
                                                        </div>
                                                    </div>
                                                    <div className="mt-4 flex">
                                                        <div className="block text-sm font-medium flex-shrink-0 w-[150px] flex-grow">WFS-Server:</div>
                                                        <div className="flex items-center gap-2 bg-gray-200 relative flex-shrink p-4 rounded-xl mt-2">
                                                            <span className="text-sm text-gray-900/60 flex-shrink break-all pr-5 ">
                                                                {`https://6tkb6m5vzc.execute-api.eu-central-1.amazonaws.com/dev/wfs/1.0/maps/${currentEntity.accessId}?request=GetCapabilities`}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => {navigator.clipboard.writeText(`https://6tkb6m5vzc.execute-api.eu-central-1.amazonaws.com/dev/wfs/1.0/map/${currentEntity.accessId}?request=GetCapabilities`); setCopied(true)}}
                                                                className="p-1.5 bg-gray-600 text-white absolute right-2 top-2 rounded-md hover:bg-gray-700 text-xs"
                                                            >
                                                                {(copied === null) ? (<MdContentCopy />) : (<MdCheck className={"text-green"} />)}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>



                                            {(isAccessActive === true) ? (
                                                <div className="p-8 sm:flex-row-reverse flex w-full justify-between flex-col-reverse gap-4">
                                                    <div>
                                                        <button
                                                            type="button"
                                                            onClick={generateCredentials}
                                                            className="text-center truncate items-center rounded-md bg-white text-gray-900 border-white border-2 disabled:text-gray-600/40 disabled:border-gray-500 px-4 py-3 text-sm font-semibold"
                                                        >
                                                            Zugangsdaten ändern
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="text-center truncate items-center rounded-md bg-white text-gray-900 border-gray-900 border-2 disabled:text-gray-600/40 disabled:border-gray-500 px-4 py-3 text-sm font-semibold"
                                                            onClick={handleSaveAccess}
                                                        >
                                                            Speicher
                                                        </button>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="text-center truncate items-center rounded-md bg-white text-red border-red border-2 disabled:text-gray-600/40 disabled:border-gray-500 px-4 py-3 text-sm font-semibold"
                                                        onClick={handleToggleAccess}
                                                    >
                                                        WFS Zugriff löschen
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="p-8 sm:flex-row-reverse flex w-full flex-col-reverse gap-4">
                                                    <button
                                                        type="button"
                                                        className="text-center truncate items-center rounded-md bg-white text-gray-900 border-white border-2 disabled:text-gray-600/40 disabled:border-gray-500 px-4 py-3 text-sm font-semibold"
                                                        onClick={handleToggleAccess}
                                                    >
                                                        WFS Zugriff aktivieren
                                                    </button>
                                                </div>
                                            ) }

                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};
